import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axiosInstance from '../../../../api';
import TopBar from '../../Tools/Topbar';
import LeftSide from '../../Tools/LeftSidebar';
import Settings from '../../Tools/Setting/Settings';
import useSettings from '../../Tools/Setting/useSettings';
import Pagination from '../../Tools/Pagination';
import { format } from 'date-fns';
import EditCaseModal from './Editcasemodal/EditNewCaseModal';
import TransferCaseModal from './Editcasemodal/TranferCaseaddmodal';

// Fetch and cache date/time formats once
const DateTimeFormatFetcher = () => {
  useEffect(() => {
    const fetchSettings = async () => {
      try {
        const authToken = sessionStorage.getItem('auth_token');
        if (!authToken) return;

        const res = await axiosInstance.get('/ems/get-system-setting', {
          headers: { Authorization: `Bearer ${authToken}` },
        });

        if (res.data?.status === 'success' && res.data.data.length > 0) {
          const data = res.data.data[0];
          sessionStorage.setItem('date_format', data.date_format || 'DD/MM/YYYY');
          sessionStorage.setItem('time_format', data.time_format || '24');
        }
      } catch (err) {
        console.error('Error fetching date/time formats:', err);
        sessionStorage.setItem('date_format', 'DD/MM/YYYY');
        sessionStorage.setItem('time_format', '24');
      }
    };
    fetchSettings();
  }, []);
  return null;
};

const EMSClosedCases = () => {
  const RoleName = sessionStorage.getItem('Role_Name');
  const [isSidebarOpen, setIsSidebarOpen] = useState(true);
  const [emsCases, setEmsCases] = useState([]);
  const [filteredCases, setFilteredCases] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const limit = 10;

  // Modals
  const [showEditModal, setShowEditModal] = useState(false);
  const [showTransferModal, setShowTransferModal] = useState(false);
  const [selectedCase, setSelectedCase] = useState(null);

  // Editability cache
  const [caseEditability, setCaseEditability] = useState({});
  const [checkingEditability, setCheckingEditability] = useState(new Set());

  const roleId = sessionStorage.getItem('Role_id');
  const userId = sessionStorage.getItem('user_id');
  const navigate = useNavigate();
  const { settings, updateSetting, handleColorTheme } = useSettings();

  const dateFormat = sessionStorage.getItem('date_format') || 'DD/MM/YYYY';
  const timeFormat = sessionStorage.getItem('time_format') || '24';

  const formatCaseDate = (dateString) => {
  if (!dateString) return 'N/A';

  try {
    const dateFormat = sessionStorage.getItem('date_format') || 'DD/MM/YYYY';
    const timeFormat = sessionStorage.getItem('time_format') || '24';

    // Map your backend formats to date-fns
    const dateFormatMap = {
      'DD/MM/YYYY': 'dd/MM/yyyy',
      'MM/DD/YYYY': 'MM/dd/yyyy',
      'YYYY-MM-DD': 'yyyy-MM-dd',
    };

    const timeFormatString = timeFormat === '12' ? 'hh:mm:ss a' : 'HH:mm:ss';
    const formatString = `${dateFormatMap[dateFormat] || 'dd/MM/yyyy'} ${timeFormatString}`;

    let utcDate;

    if (dateString.includes('T')) {
      // ISO format: 2025-10-27T16:27:19.000000Z
      utcDate = new Date(dateString.endsWith('Z') ? dateString : dateString + 'Z');
    } else {
      // MySQL format: 2025-10-27 16:27:19
      utcDate = new Date(dateString.replace(' ', 'T') + 'Z'); // Force UTC
    }

    // Extract UTC parts manually to avoid local timezone shift
    const formatted = format(
      new Date(
        utcDate.getUTCFullYear(),
        utcDate.getUTCMonth(),
        utcDate.getUTCDate(),
        utcDate.getUTCHours(),
        utcDate.getUTCMinutes(),
        utcDate.getUTCSeconds()
      ),
      formatString
    );

    return formatted;
  } catch (err) {
    console.error('Error formatting alarm time:', err, dateString);
    return 'Invalid Date';
  }
};

  const toggleSidebar = () => setIsSidebarOpen(!isSidebarOpen);

  const handleViewDetails = (caseItem) => {
    sessionStorage.setItem('currentCaseId', caseItem.id);
    sessionStorage.setItem('ClosedTabCaseStatus', caseItem.casestatus);
    navigate('/ems/closed-case-detail');
  };

  // Check edit permission from server
  const checkIfCaseIsEditable = async (caseId) => {
    if (caseEditability[caseId] !== undefined) return caseEditability[caseId];

    setCheckingEditability(prev => new Set(prev).add(caseId));

    try {
      const authToken = sessionStorage.getItem('auth_token');
      const res = await axiosInstance.get(`/ems/is-available-for-edit/${caseId}`, {
        headers: { Authorization: `Bearer ${authToken}` },
      });
      const isEditable = res.data.status === true;
      setCaseEditability(prev => ({ ...prev, [caseId]: isEditable }));
      return isEditable;
    } catch (err) {
      console.error('Edit check failed:', err);
      setCaseEditability(prev => ({ ...prev, [caseId]: false }));
      return false;
    } finally {
      setCheckingEditability(prev => {
        const next = new Set(prev);
        next.delete(caseId);
        return next;
      });
    }
  };

  // AUTO REFRESH: Re-check visible cases whenever list updates or page changes
  useEffect(() => {
    if (isLoading || emsCases.length === 0) return;

    const visibleCases = filteredCases.slice((page - 1) * limit, page * limit);
    visibleCases.forEach(item => {
      checkIfCaseIsEditable(item.id);
    });
  }, [isLoading, emsCases, page]);

  // BONUS: Re-check when user returns to tab (e.g. 2-hour window expired while away)
  useEffect(() => {
    const handleFocus = () => {
      if (emsCases.length === 0) return;

      const visibleCases = filteredCases.slice((page - 1) * limit, page * limit);
      visibleCases.forEach(item => {
        setCaseEditability(prev => {
          const updated = { ...prev };
          delete updated[item.id]; // Force fresh check
          return updated;
        });
        checkIfCaseIsEditable(item.id);
      });
    };

    window.addEventListener('focus', handleFocus);
    return () => window.removeEventListener('focus', handleFocus);
  }, [emsCases, page, filteredCases]);

  // Fetch closed cases
  const fetchClosedCases = async () => {
    try {
      setIsLoading(true);
      const authToken = sessionStorage.getItem('auth_token');

      if (!roleId || !authToken) {
        setError('Invalid session. Please log in again.');
        navigate('/login');
        return;
      }

      const response = await axiosInstance.get(`/ems/records/${roleId}/closed_cases/${userId}`, {
        headers: { Authorization: `Bearer ${authToken}` },
      });

      const data = response.data.data || response.data;
      const casesArray = Array.isArray(data) ? data : [];

      const formattedCases = casesArray.map((item) => ({
        id: item.id,
        caseLocation: item.location?.location_name || 'N/A',
        nearestAmbulance: item.ambulance?.plate_number || 'N/A',
        frommedicalcenter: item.from_medical_center?.name || 'N/A',
        nearestMedicalCenter: item.medical_center?.name || 'N/A',
        caseName: item.case_name || 'N/A',
        gender: item.gender || 'N/A',
        age: item.age || 'N/A',
        injuryType: item.injury_type || 'N/A',
        contactNumber: item.contact_number || 'N/A',
        emsMemberName: item.ems_member_name || 'N/A',
        casestatus: item.case_status || 'N/A',
        dateTime: item.created_at ? formatCaseDate(item.created_at) : 'N/A',
        closedAt: item.closed_at ? formatCaseDate(item.closed_at) : 'N/A',
        patientSituation: item.patient_situation || 'Details',
      }));

      setEmsCases(formattedCases);
      setFilteredCases(formattedCases);
      setTotal(formattedCases.length);
      setError(null);
    } catch (err) {
      console.error('Error fetching closed cases:', err);
      setError(err.response?.data?.message || 'Failed to fetch closed cases.');
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchClosedCases();
  }, []);

  useEffect(() => {
    setTotal(filteredCases.length);
  }, [filteredCases]);

  // Edit Normal Case
  const openEditNormalCaseModal = (caseId) => {
    setSelectedCase({ id: caseId }); // Just pass ID
    setShowEditModal(true);
  };

  // Edit Transfer Case
  const openEditTransferCaseModal = async (caseId) => {
    try {
      setIsLoading(true);
      const authToken = sessionStorage.getItem('auth_token');
      const response = await axiosInstance.get(`/ems/tranfer/cases/${caseId}`, {
        headers: { Authorization: `Bearer ${authToken}` },
      });

      const rawCase = response.data.data || response.data;
      const normalizedCase = {
        id: rawCase.id,
        case_name: rawCase.case_name || '',
        diagnose: rawCase.diagnose || '',
        bracelet_id: rawCase.bracelet_id || '',
        doctor: rawCase.doctor || '',
        doctor_name: rawCase.doctor_name || '',
        age: rawCase.age || '',
        gender: rawCase.gender || '',
        transfer_from_medical_center: rawCase.transfer_from_medical_center || '',
        nearest_medical_center: rawCase.nearest_medical_center || '',
        documents: rawCase.documents || {},

        // Critical: Pass patient_situation so modal can read clinic_no, request_details, escort_team
        patient_situation: rawCase.patient_situation || {
          clinic_no: '',
          request_details: '',
          escort_team: { escort_physician: '', escort_nurse: '', escort_paramedic: '', other: '' }
        }
      };

      setSelectedCase(normalizedCase);
      setShowTransferModal(true);
    } catch (err) {
      console.error('Failed to fetch transfer case:', err);
      alert('Could not load transfer case.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleEditClick = async (caseItem) => {
    const isTransferCase = caseItem.casestatus == 2;
    if (isTransferCase) {
      setSelectedCase({ id: caseItem.id }); // Only pass ID
      setShowTransferModal(true);
    } else {
      await openEditNormalCaseModal(caseItem.id);
    }
  };

  const getCurrentItems = () => {
    const start = (page - 1) * limit;
    return filteredCases.slice(start, start + limit);
  };

  const statusMap = { 1: 'New Case', 2: 'Transfer Case' };

  const tableFields = [
    { label: 'ID', key: 'id', render: (v, item) => <button className="text-primary border-0 bg-transparent p-0" onClick={() => handleViewDetails(item)}>{v}</button> },
    { label: 'Case name', key: 'caseName', render: (v, item) => <button className="text-primary border-0 bg-transparent p-0" onClick={() => handleViewDetails(item)}>{v}</button> },
    { label: 'Transfer From', key: 'frommedicalcenter' },
    { label: 'Transfer To', key: 'nearestMedicalCenter' },
    { label: 'Created On', key: 'dateTime' },
    { label: 'Closed On', key: 'closedAt' },
    { label: 'Status', key: 'casestatus', render: (v) => statusMap[v] || 'Unknown' },
    {
      label: 'Action',
      key: 'id',
      render: (_, item) => {
        const canEdit = caseEditability[item.id] === true;
        const cannotEdit = caseEditability[item.id] === false;
        const isChecking = checkingEditability.has(item.id);

        return (
          <div className="d-flex gap-3 align-items-center">

            {/* View Details */}
            <button
              className="h4 border-0 bg-transparent text-success"
              onClick={() => handleViewDetails(item)}
              title="View Details"
            >
              <i className="ti ti-list-details"></i>
            </button>

            {/* Edit Button - Always visible */}
            {/* Edit Button — visible only for EMS Team (4) and Admin (1) */}
            {(RoleName === "Admin" || RoleName === "EMS Team") && (
              <button
                className={`h4 border-0 bg-transparent ${canEdit ? 'text-warning' : 'text-muted'}`}
                onClick={() => canEdit && handleEditClick(item)}
                disabled={!canEdit || isLoading}
                title={
                  isChecking
                    ? 'Checking edit permission...'
                    : cannotEdit
                      ? 'Editing not allowed (more than 2 hours after closing)'
                      : 'Edit Case'
                }
                style={{
                  cursor: canEdit ? 'pointer' : 'not-allowed',
                  opacity: cannotEdit ? 0.5 : 1
                }}
              >
                {isChecking ? (
                  <div className="spinner-border spinner-border-sm" role="status">
                    <span className="visually-hidden">Loading...</span>
                  </div>
                ) : (
                  <i className="ti ti-edit"></i>
                )}
              </button>
            )}

          </div>
        );
      }
    },
  ];

  const currentItems = getCurrentItems();

  return (
    <div id="main-wrapper">
      <DateTimeFormatFetcher />
      <LeftSide isSidebarOpen={isSidebarOpen} toggleSidebar={toggleSidebar} />
      <div className={`page-wrapper ${isSidebarOpen ? '' : 'sidebar-closed'}`}>
        <TopBar toggleSidebar={toggleSidebar} />
        <div className="body-wrapper">
          <div className="container-fluid">
            <Settings updateSetting={updateSetting} handleColorTheme={handleColorTheme} settings={settings} />

            {error && (
              <div className="alert alert-danger alert-dismissible fade show" role="alert">
                {error}
                <button type="button" className="btn-close" onClick={() => setError(null)}></button>
              </div>
            )}

            <div className="card w-100 position-relative overflow-hidden">
              <div className="px-4 py-3">
                <h5 className="card-title fw-semibold mb-0 lh-sm">EMS Closed Cases</h5>
                <p className="card-subtitle mb-0 mt-1">View and track all EMS Closed Cases</p>
              </div>

              <div className="card-body p-4">
                {isLoading ? (
                  <div className="text-center py-5">
                    <div className="spinner-border text-primary" role="status">
                      <span className="visually-hidden">Loading...</span>
                    </div>
                    <p className="mt-2">Loading closed cases...</p>
                  </div>
                ) : (
                  <>
                    <div className="d-flex justify-content-between align-items-center mb-3">
                      <span className="text-muted">Showing {currentItems.length} of {total} cases</span>
                    </div>

                    <div className="table-responsive rounded-2 ros-table">
                      <table className="table text-nowrap customize-table mb-0 align-middle">
                        <thead className="text-dark fs-4">
                          <tr>
                            {tableFields.map((field, i) => (
                              <th key={i}><h6 className="fw-semibold mb-0">{field.label}</h6></th>
                            ))}
                          </tr>
                        </thead>
                        <tbody>
                          {currentItems.length === 0 ? (
                            <tr>
                              <td colSpan={tableFields.length} className="text-center py-4">
                                {emsCases.length === 0 ? 'No Closed Cases Found' : 'No cases on this page'}
                              </td>
                            </tr>
                          ) : (
                            currentItems.map(item => (
                              <tr key={item.id}>
                                {tableFields.map((field, i) => (
                                  <td key={i}>
                                    {field.render ? field.render(item[field.key], item) : item[field.key]}
                                  </td>
                                ))}
                              </tr>
                            ))
                          )}
                        </tbody>
                      </table>
                    </div>

                    {total > limit && (
                      <div className="mt-4">
                        <Pagination total={total} limit={limit} page={page} onPageChange={setPage} disabled={isLoading} />
                      </div>
                    )}
                  </>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Normal Case Edit Modal */}
      {selectedCase && showEditModal && (
        <EditCaseModal
          show={showEditModal}
          onClose={() => {
            setShowEditModal(false);
            setSelectedCase(null);
          }}
          caseId={selectedCase.id}
          refreshList={fetchClosedCases}
        />
      )}
      {/* Transfer Case Edit Modal */}
      {selectedCase && showTransferModal && (
        <TransferCaseModal
          show={showTransferModal}
          onHide={(saved = false) => {
            setShowTransferModal(false);
            setSelectedCase(null);
            if (saved) fetchClosedCases();
          }}
          mode="edit"
          transferCaseData={selectedCase}
          refreshList={fetchClosedCases}
        />
      )}
    </div>
  );
};

export default EMSClosedCases;